import cn from 'classnames';
import { ReactNode, useEffect, useRef } from 'react';
import { ButtonClose, ButtonPrimary } from '@ui/button';
import { Portal } from './portal';
import { Loader } from '@ui/loader';

import styles from './styles.module.sass';

export interface IModalDialogProps extends IDialogProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  actionsClassName?: string;
  headerClassName?: string;
  contentClassName?: string;
}

export function ModalDialog(props: IModalDialogProps) {
  const {
    className = '',
    children,
    title,
    loading,
    description,
    actions,
    actionsClassName = '',
    headerClassName = '',
    contentClassName = '',
    onClose,
    onOverlay,
  } = props;
  const rootClass = cn(className, styles.root);

  return (
    <Dialog
      className={rootClass}
      loading={loading}
      onClose={onClose}
      onOverlay={onOverlay}
    >
      <header className={`${styles.header} ${headerClassName}`}>
        <div>
          <Title>{title}</Title>
          <Description>{description}</Description>
        </div>
        <div className={styles.close}>
          <ButtonClose onClick={onClose} />
        </div>
      </header>

      <div
        className={`${styles.content} ${contentClassName}`}
        tabIndex={0}
        role="region"
      >
        {children}
      </div>

      <Actions className={actionsClassName}>
        {actions || <ButtonPrimary label="Закрыть" onClick={onClose} />}
      </Actions>
    </Dialog>
  );
}

function Dialog(props: IDialogProps) {
  const {
    className = '',
    loading = false,
    children,
    onClose = () => null,
    onOverlay = () => null,
  } = props;
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.body.classList.add(styles.modalVisible);
    document.addEventListener('keydown', onEscape);

    return () => {
      document.body.classList.remove(styles.modalVisible);
      document.removeEventListener('keydown', onEscape);
    };
  }, []);

  useEffect(() => {
    //dialogRef.current?.focus();
  }, [dialogRef.current]);

  return (
    <Portal>
      <div ref={dialogRef} className={styles.dialog} role="dialog" tabIndex={-1}>
        <div className={`${styles.modal} ${className}`} role="document">
          {children}
          {loading && <Loader className={styles.modal__loader} />}
        </div>

        <div className={styles.overlay} onClick={onOverlay}></div>
      </div>
    </Portal>
  );
}

interface IDialogProps {
  className?: string;
  loading?: boolean;
  children: ReactNode | ReactNode[];
  onClose?: () => void;
  onOverlay?: () => void;
}

const Title = ({ children }: { children: ReactNode }) => (
  <h3 className={styles.title}>{children}</h3>
);
const Description = ({ children }: { children: ReactNode }) => (
  <div className={styles.description}>{children}</div>
);

const Actions = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => <div className={cn([styles.actions, className])}>{children}</div>;
